import { memo, useEffect, useState } from "react";
import { Check, ChevronDown, ChevronRight, RotateCcw, X } from "lucide-react";
import { DismissPopover } from "./DismissPopover";
import { RouteToConceptPopover } from "./RouteToConceptPopover";
import { BestAngleBadge } from "@/components/angles/BestAngleBadge";
import type { PaperWithRole } from "@/types/paper";
import type { FacetSummary } from "@/types/concept";

interface Props {
  paper: PaperWithRole;
  conceptId: string;
  onTriage: (paperId: string, action: "keep" | "dismiss" | "restore") => void;
  onDismissWithReason?: (paperId: string, reason: string, reasonText?: string, findSimilar?: boolean) => void;
  isFocused?: boolean;
  isAutoDismissed?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (paperId: string, shiftKey?: boolean) => void;
  onRowClick?: (e: React.MouseEvent) => void;
  onFocus?: () => void;
  onInbox?: (paperId: string, mode: "copy" | "move") => void;
  angles?: FacetSummary[];
}

function effectiveScore(p: PaperWithRole): number {
  return p.user_score_override ?? p.normalized_score;
}

function scoreBadgeColor(score: number): string {
  if (score >= 60) return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
  if (score >= 30) return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
  return "bg-zinc-500/20 text-zinc-400 border-zinc-500/30";
}

function formatAuthors(authors: string[]): string {
  if (!authors || authors.length === 0) return "Unknown authors";
  if (authors.length === 1) return authors[0];
  return `${authors[0]} et al.`;
}

export const MonitorInboxItem = memo(function MonitorInboxItem({
  paper,
  conceptId,
  onTriage,
  isFocused = false,
  isAutoDismissed = false,
  isSelected = false,
  onDismissWithReason,
  onToggleSelect,
  onRowClick,
  onFocus,
  onInbox,
  angles,
}: Props) {
  const [manualExpanded, setManualExpanded] = useState(false);
  // Reset manual expand when focus moves away
  useEffect(() => {
    if (!isFocused) setManualExpanded(false);
  }, [isFocused]);
  // Auto-expand when focused, or manually toggled
  const expanded = isFocused || manualExpanded;
  const score = effectiveScore(paper);

  return (
    <div
      data-testid="inbox-item"
      className={`group rounded-lg border px-3 py-2 transition-colors ${
        isSelected
          ? "border-primary/50 bg-primary/10 ring-1 ring-primary/30"
          : isFocused
            ? "border-primary/50 bg-primary/5 ring-1 ring-primary/30"
            : "border-border hover:border-border/80 hover:bg-muted/30"
      }`}
      onClick={(e) => {
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          // Modifier click: selection behavior
          e.preventDefault();
          onRowClick?.(e);
        } else {
          // Plain click: set this row as focused/active
          onFocus?.();
        }
      }}
    >
      <div className="flex items-center gap-3">
        {/* Checkbox (only for non-auto-dismissed) */}
        {!isAutoDismissed && onToggleSelect && (
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => {/* handled by onClick */}}
            className="size-4 shrink-0 cursor-pointer rounded border-border accent-primary"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(paper.id, e.shiftKey);
            }}
          />
        )}

        {/* Score badge */}
        <span
          className={`flex size-10 shrink-0 items-center justify-center rounded-md border text-sm font-bold tabular-nums ${scoreBadgeColor(score)}`}
        >
          {Math.round(score)}
        </span>

        {/* Paper info */}
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setManualExpanded(!manualExpanded)}
            className="flex w-full items-start gap-1.5 text-left"
          >
            {expanded ? (
              <ChevronDown className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium leading-tight">
                {paper.title}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {formatAuthors(paper.authors)}
                {paper.year ? ` (${paper.year})` : ""}
              </p>
            </div>
          </button>
          <BestAngleBadge paper={paper} angles={angles} />

          {/* Chips */}
          {(paper.argumentation_role || paper.contribution_type) && (
            <div className="mt-1 flex flex-wrap gap-1 pl-5">
              {paper.argumentation_role && (
                <span className="rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] text-blue-400">
                  {paper.argumentation_role}
                </span>
              )}
              {paper.contribution_type && (
                <span className="rounded-full bg-purple-500/10 px-2 py-0.5 text-[10px] text-purple-400">
                  {paper.contribution_type}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Restore button for auto-dismissed (always visible) */}
        {isAutoDismissed && (
          <button
            type="button"
            onClick={() => onTriage(paper.id, "restore")}
            className="shrink-0 rounded-md border border-blue-500/30 bg-blue-500/10 p-1.5 text-blue-400 transition-colors hover:bg-blue-500/20"
            title="Restore paper"
          >
            <RotateCcw className="size-4" />
          </button>
        )}
      </div>

      {/* Expanded details + action buttons */}
      {expanded && (
        <div className="mt-2 space-y-2 pl-[3.25rem]">
          {paper.relevance_justification && (
            <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-1.5">
              <p className="text-[11px] font-medium text-primary/70">Scoring rationale</p>
              <p className="text-xs leading-relaxed text-muted-foreground">{paper.relevance_justification}</p>
            </div>
          )}
          {paper.abstract && (
            <div className="rounded-md bg-muted/50 px-3 py-2 text-xs leading-relaxed text-muted-foreground">
              {paper.abstract}
            </div>
          )}

          {/* Action buttons — below content, only when expanded */}
          {!isAutoDismissed && (
            <div className="flex items-center gap-1.5 pt-1">
              <button
                type="button"
                onClick={() => onTriage(paper.id, "keep")}
                className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs text-emerald-400 transition-colors hover:bg-emerald-500/20"
                title="Keep paper (Enter)"
              >
                <Check className="mr-1 inline size-3" />
                Keep
              </button>
              <RouteToConceptPopover
                conceptId={conceptId}
                paperId={paper.id}
                isFoundational={false}
                onRouted={() => onFocus?.()}
                onInbox={onInbox ? (mode) => onInbox(paper.id, mode) : undefined}
              />
              {onDismissWithReason ? (
                <DismissPopover
                  onDismiss={(reason, reasonText, findSimilar) =>
                    onDismissWithReason(paper.id, reason, reasonText, findSimilar)
                  }
                />
              ) : (
                <button
                  type="button"
                  onClick={() => onTriage(paper.id, "dismiss")}
                  className="rounded-md border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-xs text-red-400 transition-colors hover:bg-red-500/20"
                  title="Dismiss paper (d)"
                >
                  <X className="mr-1 inline size-3" />
                  Dismiss
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
